import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { FilterMatchMode } from "primereact/api";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { Column } from "primereact/column";
import { FormSkeleton, Header } from "../../components";
import { Button } from "iuvity-ui-components/dist/button";
import { FilterDialog } from "../../components/environments/FilterDialog";
import { TableHeader } from "../../components/datatable/TableHeader";
import { useEnvironments } from "../../hooks/useEnvironments";
import { AppContext } from "../../context/index";
import {
  DialogProps,
  CustomDialog,
} from "../../components/dialog/CustomDialog";
import { Tag } from "iuvity-ui-components/dist/tag";
import { capitalizeFirstLetter } from "../../utils";
import { formatDateToLocale } from "../../helpers/formatDate";
import { EmptyTemplate } from "../../components/datatable/EmptyTemplate";
import noResultsImage from "../../assets/Illustration_NoResults.svg";
import { InputText } from "iuvity-ui-components/dist/inputtext";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from "iuvity-ui-components";

const filterDefaultValues = {
  functionality: [],
  version: "",
  name: "",
  type: "",
  status: "",
  createdat: "",
  updatedat: ""



};
const formBlankValues = {
  functionality: [],
  version: "",
  name: "",
  type: "",
  status: "",
  createdat: "",
  updatedat: ""

};

export const EnvExportPage = () => {
  const navigate = useNavigate();
  const {
    t,
    appState: { language, user, pageFilter, selectedEnvironment },
    setAppState,
  } = useContext(AppContext);


  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: formBlankValues,
  });
  const { control, setValue, getValues } = form;

  const { data, envAction, reloadFn } = useEnvironments("EXPORTINFO");
  // const { dataExportGeo: itemsGeoToExport } = useExportImportGeo("DATA");
  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    functionality: { value: pageFilter?.functionality, matchMode: FilterMatchMode.IN },
    type: { value: pageFilter?.type, matchMode: FilterMatchMode.IN },
    name: { value: pageFilter?.name, matchMode: FilterMatchMode.CONTAINS },
    status: { value: pageFilter?.status, matchMode: FilterMatchMode.EQUALS },
    version: { value: pageFilter?.version, matchMode: FilterMatchMode.IN },
    createdat: { value: pageFilter?.createdat, matchMode: FilterMatchMode.CONTAINS },
    updatedat: { value: pageFilter?.updatedat, matchMode: FilterMatchMode.CONTAINS },
  }
  const [exportFilters, setExportFilters] = useState(defaultFilterValues);
  const columns = useMemo(() => {
    return [
      {
        field: "functionality",
        header: t("Environments.functionalityLabel"),
      },
      {
        field: "type",
        header: t("Environments.typeLabel"),
      },
      {
        field: "name",
        header: t("Environments.nameLabel"),
      },
      {
        field: "status",
        header: t("Environments.statusLabel"),
      },
      {
        field: "version",
        header: t("Environments.versionLabel"),
      },
      {
        field: "createdat",
        header: t("Environments.createdatLabel"),
      },
      {
        field: "updatedat",
        header: t("Environments.updatedatLabel"),
      },
    ];
  }, [t]);

  const [filteredData, setFilteredData] = useState<any[] | null>(null);
  const [filterModalVisible, setFilterModalVisible] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const onClearFilters = () => {
    setFilteredData(data);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(filterDefaultValues);
  };

  const formOptions = useMemo(() => {
    if (data) {
      return {
        functionality: [...new Set(data.map((item: any) => item.functionality))]
          .map((item) => ({
            value: item,
            label: t(`Environments.${item}Label`),
            key: item,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
        version: [...new Set(data.map((item: any) => item.version))].map(
          (item) => ({
            value: `${item}`,
            label: `${item}`,
          })
        ),
        type: [...new Set(data.map((item: any) => item.type))].map(
          (item) => ({
            value: `${item}`,
            label: t(`Environments.${item}Label`),
          })
        ),
        status: [...new Set(data.map((item: any) => item.status))].map(
          (item: any) => ({
            value: `${item}`,
            label: t(`Environments.${item.toLowerCase()}StatusLabel`).toUpperCase(),
          })
        )
      };
    } else {
      return;
    }
  }, [t, data]);

  const handleModalProps = () => {
    return setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: t(`Applications.exportModalTitle`),
      content: t(`Applications.exportModalContent`),
      onConfirm: onExportApp,
    }));
  };

  const onExportApp = async () => {
    const { functionality, version, status } = form.getValues();
    const statusFilter: any = status != null || status != ''
      ? formOptions?.status.filter(item => status == item.label).map(item => item.value)
      : null;

    const functionalities: any = functionality.length > 0
      ? functionality
      : formOptions?.functionality.map((item) => item.key!)!;
    const itemsToExport = functionalities.reduce(
      (arr: any, el: any) => ({
        ...arr,
        [el]: true,
      }),
      {}
    );
    if (Object.keys(itemsToExport).includes("products"))
      (itemsToExport as any)["productsinfo"] = true;
    await envAction("EXPORT", {
      envFullName: `${selectedEnvironment?.fullname}_${t(
        "Environments.filenameExtraInfo"
      )}_${new Date().toISOString().substring(0, 10).replace(/-|:/g, "") +
      "-" +
      new Date().toLocaleTimeString("es-ES").replace(/-|:/g, "")
        }`,
      numExportItems: filteredData?.length || 0,
      appData: {
        appid: selectedEnvironment?.applicationid,
        envid: selectedEnvironment?.id,
        version:
          version.length > 0
            ? version
            : formOptions?.version.map((item) => item.value!)!,
      },
      filters: { ...getValues(), status: statusFilter },
      itemsToExport,
      itemsGeoToExport: [],
    });
  };

  useEffect(() => {
    if (data) setFilteredData(data);
  }, [data]);

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`Environments.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const statusBodyTemplate = (rowData: any) => (
    <Tag
      value={capitalizeFirstLetter(rowData.status)}
      severity={
        (['active', 'activo'].includes(rowData.status.toLowerCase()) && 'success') ||
        (['draft', 'borrador'].includes(rowData.status.toLowerCase()) && 'info') ||
        (['inactive', 'inactivo'].includes(rowData.status.toLowerCase()) && 'danger') ||
        'warning'
      }
    />
  );
  const functionalityBodyTemplate = (rowData: any) => (
    t(`Environments.${rowData.functionality}Label`)
  );

  const itemTemplate = (rowData: any) => {
    console.log(rowData);
    if (rowData.field == 'status') return statusBodyTemplate(rowData);
    if (rowData.field == 'functionality') return functionalityBodyTemplate(rowData);
    return undefined;
  }
  const onReturn = () => {
    navigate(-1);
    window.scrollTo(0, 0);
  };
  const onClearFiltersColumn = () => {
    setExportFilters(defaultFilterValues);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(formBlankValues);
  };

  const filterClearTemplate = (options: any) => {
    return (
      <Button
        label={t("Environments.clearFilterLabel")}
        variant="p-button-text"
        onClick={() => { onClearFiltersColumn(); }}
      />
    )
  };

  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        label={t("Environments.applyFilterLabel")}
        className="p-button"
        onClick={() => {
          options.filterApplyCallback();
          let valueFilters = getValues();

          setAppState((prev: any) => ({
            ...prev,
            pageFilter: valueFilters,
          }));
        }}
      />
    )
  };

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option}</span>
      </div>
    );
  };

  const multiselectFilterTemplate = (options: any, translationText = '{text}') => {
    return (
      <Controller
        name={options.field}
        control={control}
        // rules={PermissionsFormSchema(t).name}
        render={({ field }) => {
          field.onChange = (e) => {
            console.log(e.value)
            setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
            options.filterCallback(e.value);
          };
          return (
            <>
              <label htmlFor={options.field} className="block lb mt-2 mb-1">
                {t(`Environments.${options.field}Label`)}
              </label>
              <MultiSelect
                display="chip"
                options={(formOptions as any)[options.field]}
                //itemTemplate={ItemsTemplate}
                placeholder={t(`Environments.${options.field}PlaceholderLabel`)}
                className="mb-2"
                emptyFilterMessage={t("Environments.noAvailableMessage")}
                filter
                {...field}
              />
            </>
          );
        }}
      />
    )
  };
  const functionalityFilterTemplate = (options: any, translationText = '{text}') => {
    return (
      <Controller
        name={options.field}
        control={control}
        // rules={PermissionsFormSchema(t).name}
        render={({ field }) => {

          field.onChange = (e) => {
            const valuesToUpdate = [...e.value];

            if (
              valuesToUpdate.some(
                (item: string) =>
                  ["products", "transfers"].includes(item) &&
                  !e.value.includes("mappedfields")
              )
            ) {
              valuesToUpdate.push("mappedfields");
            }

            if (
              valuesToUpdate.includes("products") &&
              !valuesToUpdate.includes("productproperties")
            ) {
              valuesToUpdate.push("productproperties");
            }

            if (
              valuesToUpdate.includes("transfers") &&
              !valuesToUpdate.includes("transactionproperties")
            ) {
              valuesToUpdate.push("transactionproperties");
            }
            setValue(field.name, valuesToUpdate, REVALIDATE_FORM_PROPS);
            options.filterCallback(valuesToUpdate);
          };
          return (
            <>
              <label htmlFor={options.field} className="block lb mt-2 mb-1">
                {t(`Environments.${options.field}Label`)}
              </label>
              <MultiSelect
                display="chip"
                options={(formOptions as any)[options.field]}
                //itemTemplate={ItemsTemplate}
                placeholder={t(`Environments.${options.field}PlaceholderLabel`)}
                className="mb-2"
                emptyFilterMessage={t("Environments.noAvailableMessage")}
                filter
                {...field}
              />
            </>
          );
        }}
      />
    )
  };
  const inputTextFilterTemplate = (options: any) => {
    return (
      <Controller
        name={options.field}
        control={control}
        render={({ field }) => {
          field.onChange = (e) => {
            setValue(field.name, e.target.value);
            options.filterCallback(e.target.value);
          };
          return (
            <>
              <label htmlFor={options.field} className="block lb mt-2 mb-2">
                {t(`Environments.${options.field}Label`)}
              </label>
              <InputText
                placeholder={t(`Environments.${options.field}PlaceholderLabel`)}
                autoComplete="off"
                type={'text'}
                className="mb-2"
                {...field}
              />
            </>
          );
        }}
      />
    )
  };

  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  }
  const statuses = [
    `${t("Environments.activeStatusLabel")}`,
    `${t("Environments.inactiveStatusLabel")}`,
    `${t("Environments.draftStatusLabel")}`,
  ];
  const statusFilterTemplate = (options: any) => {
    return (
      <Controller
        name={options.field}
        control={control}
        render={({ field }) => {
          field.onChange = (e) => {
            options.filterCallback(e.value);
            setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
          };
          return (
            <>
              <label htmlFor={options.field} className="block lb mt-2 mb-3">
                {t(`Environments.${options.field}Label`)}
              </label>
              {/* // @ts-ignore */}
              <Dropdown
                options={statuses}
                itemTemplate={statusItemTemplate}
                placeholder={t(`Environments.${options.field}FilterPlaceholder`)}
                className="p-column-filter mb-2"
                showClear={false}
                {...field}
              />
            </>
          );
        }}
      />

    )
  }

  const dateFilterTemplate = (options: any) => {
    return (
      <Controller
        name={options.field}
        control={control}
        render={({ field }) => {
          field.onChange = (e) => {
            console.log(e)
            options.filterCallback(e.value);
            console.log(formatDateToLocale(e.value, language, true));
            setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
          };

          return <Calendar
            label={t(`Environments.${options.field}Label`)}
            dateFormat="mm/dd/yy"
            placeholder="mm/dd/yyyy"
            mask="99/99/9999"
            className="mt-2 mb-2"
            {...field}
          />
        }}
      />
    )
  };

  const filterTemplate = (options: any) => {
    if (options.field == 'name') return inputTextFilterTemplate(options);
    if (options.field == 'functionality') return functionalityFilterTemplate(options, `Environments.{text}Label`);
    if (options.field == 'type') return multiselectFilterTemplate(options, `Environments.{text}Label`);
    if (options.field == 'status') return statusFilterTemplate(options);
    if (options.field == 'version') return multiselectFilterTemplate(options);
    if (options.field == 'createdat') return inputTextFilterTemplate(options);
    if (options.field == 'updatedat') return inputTextFilterTemplate(options);

    return ''

  }

  useEffect(() => {
    onClearFiltersColumn()
  }, []);

  useEffect(() => {
    setExportFilters(defaultFilterValues);
  }, [pageFilter]);

  return (
    <>
      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-60px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            <span >
              {
                t("Applications.exportApplicationTitle")
              }
            </span>
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("Applications.applicationsBreadcrumbLabel")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Applications.advancedOptionsBreadcrumbLabel"),
              style: { fontWeight: "bold" },
              disabled: true
            },
            {
              label: t("Applications.applicationsBreadcrumbLabel"),
              command: () => navigate("/applications"),
            },
            {
              label: t("Applications.editApplicationTitle"),
              command: () => navigate("/applications/edit"),
            },
            {
              label: t("Applications.exportApplicationTitle"),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content" style={{ marginLeft: '-5px' }}>
          <span style={{ lineHeight: '1.5rem' }}><div dangerouslySetInnerHTML={{ __html: t('Applications.exportApplicationDescription') }} /></span>
        </div>
      </TransactionalSubheader>
      

      <BoxFull className="dt-c my-4" id="wrapper-applications-export">
        {data && filteredData ? (

          <DataTable
            header={
              <TableHeader
                title={t("Environments.configurationsTableTitle")}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={exportFilters}
                setFilters={setExportFilters}
                reload={reloadFn}
                onClearFiltersColumn={onClearFiltersColumn}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={<EmptyTemplate image={noResultsImage} text={t("TableActions.emptyMessage")} />}
            rows={10}
            size="normal"
            filters={exportFilters}
            sortField="id"
            dataKey="id"
            sortOrder={1}
            value={filteredData.map((item) => ({
              ...item,
              status: t(`Environments.${item.status.toLowerCase()}StatusLabel`),
              createdat: formatDateToLocale(item.createdat, language, true),
              updatedat: formatDateToLocale(item.updatedat, language, true),
              type: t(`Environments.${item.type}Label`),
            }))}
            globalFilterFields={[
              "functionality",
              "version",
            ]}
            filterDisplay="menu"
          >
            {selectedColumns.map((col) => {
              return (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  sortable={!(col.field === "actions")}
                  align={col.field === "actions" ? "center" : "left"}
                  body={col.field=='status'?statusBodyTemplate: col.field=='functionality'?functionalityBodyTemplate:undefined}
                  filterField={col.field}
                  showFilterMatchModes={false}
                  filterMenuStyle={{ width: "23rem" }}
                  filter={['functionality', 'version'].includes(col.field)}
                  filterApply={filterApplyTemplate}
                  filterClear={filterClearTemplate}
                  filterElement={filterTemplate}
                />
              );
            })}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
        )}

      </BoxFull>
      <BoxFull className="mt-0 mb-3 p-0" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-4">
          <Button
            label={t("Applications.returnBtnLabel")}
            variant="p-button-text"
            onClick={onReturn}
          />
          <Button
            label={t("Applications.exportBtnLabel")}
            variant="p-button"
            onClick={handleModalProps}
            disabled={!data}
          />
        </div>
      </BoxFull>
      <BoxFull className="my-5 py-0 px-0" variant="tertiary">
        <div className="flex  ">
          <div className="tablefilter-mc">
          </div>

        </div>
      </BoxFull>
      <FilterDialog
        visible={filterModalVisible}
        setVisible={setFilterModalVisible}
        data={data}
        setData={setFilteredData}
        onClearFilters={onClearFilters}
        form={form}
        formOptions={formOptions}
      />

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
